import { useEffect } from 'react';

const useSseFallback = ({
  realtimeEnabled,
  realtimeConnected,
  queueProgramRefresh,
  setIsSyncing,
  setProgramSyncing,
  setSnackbar,
  formatAbortSummary,
  queryClient,
}) => {
  useEffect(() => {
    if (realtimeEnabled && realtimeConnected) return undefined;
    let reconnectTimer;
    let source;

    const connect = () => {
      source = new EventSource('/api/updates/stream');

      const handleProgramRefresh = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          if (!payload.programName) return;
          queueProgramRefresh(payload.programName);
        } catch (err) {
          console.error('Failed to parse refresh program payload:', err);
        }
      };

      const handleRefreshStatus = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          setIsSyncing(payload?.status === 'in_progress');
        } catch (parseError) {
          console.warn('Failed to parse refresh-status payload', parseError);
        }
        queryClient.invalidateQueries({ queryKey: ['cacheStatus'] });
      };

      const handleProgramStatus = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const programs = payload?.programs || {};
          const activeProgram = Object.values(programs).some((status) => status === 'in_progress');
          setProgramSyncing(activeProgram);
        } catch (parseError) {
          console.warn('Failed to parse program-status payload', parseError);
        }
        queryClient.invalidateQueries({ queryKey: ['cacheStatus'] });
      };

      const handleProgramError = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          if (!payload?.programName) return;
          setSnackbar({
            open: true,
            message: `${payload.programName} refresh issue: ${payload.message || 'Unknown error'}`,
            severity: 'warning',
          });
        } catch (parseError) {
          console.warn('Failed to parse program-refresh-error payload', parseError);
        }
      };

      const handleRefreshAborted = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          setIsSyncing(false);
          setSnackbar({
            open: true,
            message: formatAbortSummary(payload),
            severity: 'warning',
          });
        } catch (parseError) {
          console.warn('Failed to parse refresh-aborted payload', parseError);
        }
      };

      source.addEventListener('program-refresh-complete', handleProgramRefresh);
      source.addEventListener('refresh-status', handleRefreshStatus);
      source.addEventListener('program-status', handleProgramStatus);
      source.addEventListener('program-refresh-error', handleProgramError);
      source.addEventListener('refresh-aborted', handleRefreshAborted);

      source.onerror = (err) => {
        console.warn('SSE connection error', err);
        source.close();
        if (reconnectTimer) return;
        reconnectTimer = setTimeout(() => {
          reconnectTimer = undefined;
          connect();
        }, 30000);
      };

      return () => {
        source.removeEventListener('program-refresh-complete', handleProgramRefresh);
        source.removeEventListener('refresh-status', handleRefreshStatus);
        source.removeEventListener('program-status', handleProgramStatus);
        source.removeEventListener('program-refresh-error', handleProgramError);
        source.removeEventListener('refresh-aborted', handleRefreshAborted);
      };
    };

    const cleanupListeners = connect();

    return () => {
      cleanupListeners?.();
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
      source?.close();
    };
  }, [
    formatAbortSummary,
    queueProgramRefresh,
    queryClient,
    realtimeEnabled,
    realtimeConnected,
    setIsSyncing,
    setProgramSyncing,
    setSnackbar,
  ]);
};

export default useSseFallback;
